// « Preuve GPS » of a draft tour: billed arrivals and departures against the Consolidation Geotab day.
import { geotabTruckOf, minutesOf } from "./consolidationGeotabFill.js";
import { PAPER_TOLERANCE_MIN } from "./consolidationGeotabGantt.js";

const STOP_KIND = { loading: "loading", delivery: "delivery", dompeur: "delivery" };
const crewOf = (source) => source.items.find((item) => item.service_crew?.truck_label && item.service_crew?.period)?.service_crew || null;
// Orders of a billed stop: delivered there, or loaded at the mill for the deliveries that follow.
const stopOrders = (step) => new Set(step.kind === "loading"
  ? step.items.flatMap((item) => (item.service_context?.cargo_origins || []).flatMap((origin) => origin.order_references || []))
  : step.items.map((item) => item.order_reference).filter(Boolean));

/** Geotab day of a tour (« 2026-10-06@day »), or null when its truck is unknown (preload, old revision). */
export function tourGpsDay(source) {
  const crew = crewOf(source);
  return crew ? `${source.date}@${crew.period}` : null;
}

function gpsEvent(key, label, billed, real) {
  const gap = billed == null || real == null ? null : billed - real;
  return { key, label, billed, real: real ?? null, gap, ok: gap !== null && Math.abs(gap) <= PAPER_TOLERANCE_MIN };
}

/**
 * Billed stops (minutes of the dispatch day) next to their Geotab stop, and each billed time with its gap.
 * The billed road runs from the previous real departure; stops on the road are not checked (16 oct.).
 * null without a Geotab day; total 0 when the truck has no Geotab trip that day.
 */
export function tourGpsProof(source, data) {
  const crew = crewOf(source);
  if (!crew || !data) return null;
  const cmp = geotabTruckOf(data, crew)?.comparison;
  const sites = (cmp?.sites || []).filter((site) => site.real_arrival != null);
  const row = { dispatch_date: source.date, timezone: crew.timezone || source.timezone };
  const at = (value) => (value ? minutesOf(value, row) : null);
  const stops = cmp ? source.steps.filter((step) => STOP_KIND[step.kind] && step.block?.start).map((step) => {
    const orders = stopOrders(step);
    const site = sites.find((candidate) => candidate.kind === STOP_KIND[step.kind] && candidate.order_ids?.some((id) => orders.has(id)));
    const previous = site && sites.filter((candidate) => candidate.real_departure != null && candidate.real_departure <= site.real_arrival)
      .sort((a, b) => b.real_departure - a.real_departure)[0];
    return { key: step.key, kind: STOP_KIND[step.kind], name: step.site, from: previous?.name || null, offset: site?.offset_m ?? null,
      billed: { start: at(step.block.start), arrival: at(step.block.arrival), end: at(step.block.end) },
      real: site ? { start: previous ? previous.real_departure : cmp.real_departure, arrival: site.real_arrival, end: site.open_end ? null : site.real_departure } : null };
  }) : [];
  const events = [];
  for (const stop of stops) {
    // The departure from a billed stop is also the start of the next road: listed once.
    if (events.at(-1)?.billed !== stop.billed.start) {
      events.push(gpsEvent(`start:${stop.key}`, stop.from ? `Départ de ${stop.from}` : "Départ du camion", stop.billed.start, stop.real?.start));
    }
    events.push(gpsEvent(`arrival:${stop.key}`, `Arrivée · ${stop.name}`, stop.billed.arrival, stop.real?.arrival));
    events.push(gpsEvent(`end:${stop.key}`, `Départ · ${stop.name}`, stop.billed.end, stop.real?.end));
  }
  const checked = events.filter((event) => event.ok).length;
  return { stops, events, checked, total: events.length, ok: events.length > 0 && checked === events.length };
}
